import React from 'react';
import ReactDOM from 'react-dom/client';
import Select, { components } from 'react-select';
import Handsontable from 'handsontable';

const AGREE_AUDITS_VALUE = '-1';
const AGREE_AUDITS_LABEL = 'Agree Audits';
const MOBILE_BREAKPOINT = 768;
const DESKTOP_BREAKPOINT = 1280;

export class MultiSelectEditor extends Handsontable.editors.BaseEditor {
  init() {
    this.selectContainer = this.hot.rootDocument.createElement('div');
    this.selectContainer.style.position = 'absolute';
    this.selectContainer.style.zIndex = '9999';
    this.agreeAll = false;
    this.renderKey = 0;

    this.scrollListener = event => {
      if (this.selectContainer && this.selectContainer.contains(event.target)) {
        return;
      }
      this.finishEditing();
    };
  }

  getValue() {
    if (this.agreeAll) {
      return AGREE_AUDITS_VALUE;
    }
    return this.currentValue.map(option => option.id).join(',');
  }

  setValue(value) {
    this.initialValue = value;
    this.agreeAll = value === AGREE_AUDITS_VALUE;
  }

  updateSelectionCount(weight) {
    if (!this.countDisplay) return;

    this.countDisplay.innerText = `${weight}/10`;
    this.countDisplay.style.color = weight >= 10 ? '#dc3545' : weight >= 8 ? '#fd7e14' : '#6c757d';
    this.countDisplay.style.fontWeight = weight >= 10 ? '600' : '500';
  }

  clearWarningMessage() {
    if (this.warningTimeout) {
      clearTimeout(this.warningTimeout);
      this.warningTimeout = null;
    }
    if (this.warningDiv?.parentNode) {
      this.warningDiv.remove();
      this.warningDiv = null;
    }
  }

  calculateDropdownPosition() {
    const cellRect = this.TD.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Responsive dropdown width
    const minWidth = viewportWidth < DESKTOP_BREAKPOINT ? 350 : 400;
    const maxWidth = viewportWidth < DESKTOP_BREAKPOINT ? 450 : 600;
    const proportionalWidth = viewportWidth * 0.25;
    let dropdownWidth = Math.max(minWidth, Math.min(proportionalWidth, maxWidth));
    dropdownWidth = Math.max(cellRect.width, dropdownWidth);

    // Responsive dropdown height
    const baseHeaderHeight = this.isMobile ? 45 : 51;
    const baseControlHeight = this.isMobile ? 32 : 36;
    const baseMenuHeight = this.isMobile ? 280 : Math.min(320, viewportHeight * 0.4);
    const estimatedDropdownHeight = baseHeaderHeight + baseControlHeight + baseMenuHeight;

    // Calculate horizontal position
    const horizontalMargin = this.isMobile ? 8 : 5;
    let left = cellRect.left;
    if (viewportWidth - cellRect.left < dropdownWidth && cellRect.right > dropdownWidth) {
      left = cellRect.right - dropdownWidth;
    }
    left = Math.max(horizontalMargin, Math.min(left, viewportWidth - dropdownWidth - horizontalMargin));

    // Calculate vertical position
    const spaceBelow = viewportHeight - cellRect.bottom;
    const spaceAbove = cellRect.top;
    const verticalMargin = this.isMobile ? 15 : 20;
    const minHeight = this.isMobile ? 200 : 250;

    let top, maxHeight;
    if (spaceBelow >= estimatedDropdownHeight + verticalMargin) {
      // Enough space below - position dropdown below cell
      top = cellRect.bottom;
      maxHeight = estimatedDropdownHeight;
    } else if (spaceAbove >= estimatedDropdownHeight + verticalMargin) {
      // Enough space above - position dropdown above cell
      top = cellRect.top - estimatedDropdownHeight;
      maxHeight = estimatedDropdownHeight;
    } else {
      // Limited space - use available space more efficiently
      if (spaceBelow > spaceAbove) {
        // More space below - position below cell
        top = cellRect.bottom;
        maxHeight = Math.max(spaceBelow - verticalMargin, minHeight);
      } else {
        // More space above - position above cell but ensure visibility
        const availableAbove = spaceAbove - verticalMargin;
        if (availableAbove >= minHeight) {
          top = cellRect.top - availableAbove;
          maxHeight = availableAbove;
        } else {
          // Not enough space above, use below with limited height
          top = cellRect.bottom;
          maxHeight = Math.max(spaceBelow - verticalMargin, minHeight);
        }
      }
    }

    return {
      left: left + window.scrollX,
      top: top + window.scrollY,
      width: dropdownWidth,
      maxHeight
    };
  }

  setupScrollListeners() {
    const listeners = [
      [this.hot.rootElement, 'scroll'],
      [window, 'scroll'],
      [window, 'resize']
    ];

    listeners.forEach(([element, event]) => {
      element.addEventListener(event, this.scrollListener, true);
    });

    // Find scrollable parents
    this.scrollParents = [];
    let parent = this.hot.rootElement.parentElement;
    while (parent && parent !== document.body) {
      const overflow = getComputedStyle(parent).overflow;
      if (['auto', 'scroll', 'hidden'].includes(overflow)) {
        parent.addEventListener('scroll', this.scrollListener, true);
        this.scrollParents.push(parent);
      }
      parent = parent.parentElement;
    }
  }

  removeScrollListeners() {
    const listeners = [
      [this.hot.rootElement, 'scroll'],
      [window, 'scroll'],
      [window, 'resize']
    ];

    listeners.forEach(([element, event]) => {
      element.removeEventListener(event, this.scrollListener, true);
    });

    this.scrollParents?.forEach(parent => {
      parent.removeEventListener('scroll', this.scrollListener, true);
    });
    this.scrollParents = null;
  }

  createHeader() {
    const header = document.createElement('div');
    const isMobile = this.isMobile;
    Object.assign(header.style, {
      padding: isMobile ? '8px 10px' : '10px 12px',
      borderBottom: '1px solid #e9ecef',
      backgroundColor: '#f8f9fa',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      borderRadius: '6px 6px 0 0',
      minHeight: isMobile ? '40px' : '45px'
    });

    const leftSection = document.createElement('div');
    Object.assign(leftSection.style, {
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      flexWrap: 'wrap'
    });

    // Row info
    const rowInfo = document.createElement('div');
    Object.assign(rowInfo.style, {
      fontSize: isMobile ? '12px' : '13px',
      color: '#495057',
      fontWeight: '500'
    });
    rowInfo.innerText = `Row ${this.row + 1}`;

    // Agree Audits Checkbox
    const agreeContainer = document.createElement('div');
    Object.assign(agreeContainer.style, {
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    });

    const agreeCheckbox = document.createElement('input');
    agreeCheckbox.type = 'checkbox';
    agreeCheckbox.id = `agree-all-${this.row}`;
    agreeCheckbox.checked = this.agreeAll;
    agreeCheckbox.style.cursor = 'pointer';
    agreeCheckbox.style.accentColor = '#28a745';

    const agreeLabel = document.createElement('label');
    agreeLabel.htmlFor = `agree-all-${this.row}`;
    agreeLabel.innerText = AGREE_AUDITS_LABEL;
    Object.assign(agreeLabel.style, {
      fontSize: isMobile ? '12px' : '13px',
      color: '#495057',
      fontWeight: '500',
      cursor: 'pointer'
    });

    agreeContainer.append(agreeCheckbox, agreeLabel);

    agreeCheckbox.addEventListener('change', (e) => {
      this.agreeAll = e.target.checked;
      if (this.agreeAll) {
        this.currentValue = [];
      }
      this.renderKey++;
      this.renderReactComponent();
    });

    leftSection.append(rowInfo, agreeContainer);

    // Progress section
    const progressSection = document.createElement('div');
    Object.assign(progressSection.style, {
      display: 'flex',
      alignItems: 'center',
      gap: isMobile ? '6px' : '8px'
    });

    this.countDisplay = document.createElement('span');
    Object.assign(this.countDisplay.style, {
      fontSize: isMobile ? '12px' : '13px',
      fontWeight: '500'
    });

    const progressContainer = document.createElement('div');
    Object.assign(progressContainer.style, {
      width: isMobile ? '50px' : '60px',
      height: '4px',
      backgroundColor: '#e9ecef',
      borderRadius: '2px',
      overflow: 'hidden'
    });

    this.progressBar = document.createElement('div');
    Object.assign(this.progressBar.style, {
      height: '100%',
      borderRadius: '2px',
      transition: 'width 0.2s ease, background-color 0.2s ease'
    });

    progressContainer.appendChild(this.progressBar);
    progressSection.append(this.countDisplay, progressContainer);
    header.append(leftSection, progressSection);

    return header;
  }

  updateProgressBar(weight) {
    if (!this.progressBar) return;

    const percentage = Math.min((weight / 10) * 100, 100);
    this.progressBar.style.width = `${percentage}%`;

    const colors = ['#20c997', '#ffc107', '#fd7e14', '#dc3545'];
    const colorIndex = weight >= 10 ? 3 : weight >= 8 ? 2 : weight >= 5 ? 1 : 0;
    this.progressBar.style.backgroundColor = colors[colorIndex];
  }

  handleKeyDown(e, editor) {
    const keyActions = {
      'Escape': () => { e.preventDefault(); editor.finishEditing(); },
      'Enter': () => { e.preventDefault(); editor.finishEditing(); },
      'Tab': () => { e.preventDefault(); editor.finishEditing(); }
    };

    if (keyActions[e.key]) {
      keyActions[e.key]();
      return;
    }

    // Handle arrow navigation with scroll
    if (['ArrowUp', 'ArrowDown'].includes(e.key)) {
      setTimeout(() => {
        const menuList = editor.selectContainer?.querySelector('.cq-select__menu-list');
        const focusedOption = editor.selectContainer?.querySelector('.cq-select__option--is-focused');

        if (menuList && focusedOption) {
          const menuRect = menuList.getBoundingClientRect();
          const optionRect = focusedOption.getBoundingClientRect();

          if (optionRect.bottom > menuRect.bottom) {
            menuList.scrollTop += optionRect.bottom - menuRect.bottom + 5;
          } else if (optionRect.top < menuRect.top) {
            menuList.scrollTop -= menuRect.top - optionRect.top + 5;
          }
        }
      }, 0);
    }
  }

  getSelectStyles(maxMenuHeight) {
    const isMobile = this.isMobile;

    return {
      control: (provided, state) => ({
        ...provided,
        minHeight: isMobile ? '32px' : '36px',
        backgroundColor: '#ffffff',
        borderColor: state.isFocused ? '#4a90e2' : '#dee2e6',
        borderRadius: '6px 6px 0 0',
        boxShadow: state.isFocused ? '0 0 0 2px rgba(74, 144, 226, 0.1)' : 'none',
        '&:hover': { borderColor: '#4a90e2' }
      }),
      valueContainer: (provided) => ({
        ...provided,
        padding: isMobile ? '3px 6px' : '4px 8px',
        maxHeight: isMobile ? '100px' : '120px',
        overflowY: 'auto'
      }),
      menu: (provided) => ({
        ...provided,
        width: '100%',
        maxHeight: `${maxMenuHeight}px`,
        zIndex: 10001,
        boxShadow: '0 6px 20px rgba(0,0,0,0.12)',
        borderRadius: '0 0 6px 6px',
        border: '1px solid #dee2e6',
        borderTop: 'none',
        marginTop: '0'
      }),
      menuList: (provided) => ({
        ...provided,
        paddingTop: isMobile ? '3px' : '4px',
        paddingBottom: isMobile ? '3px' : '4px',
        maxHeight: `${maxMenuHeight - (isMobile ? 35 : 40)}px`
      }),
      option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isSelected ? '#4a90e2' : state.isFocused ? '#f8f9fa' : 'white',
        color: state.isSelected ? 'white' : state.isDisabled ? '#adb5bd' : '#495057',
        padding: isMobile ? '5px 6px' : '6px 8px',
        cursor: state.isDisabled ? 'not-allowed' : 'pointer',
        borderBottom: '1px solid #f8f9fa',
        fontSize: isMobile ? '13px' : '14px',
        fontWeight: state.isSelected ? '500' : '400',
        '&:last-child': { borderBottom: 'none' }
      }),
      multiValue: (provided) => ({
        ...provided,
        backgroundColor: '#e7f3ff',
        borderRadius: '4px',
        border: '1px solid #b8daff',
        margin: isMobile ? '1px' : '2px',
        fontSize: isMobile ? '12px' : '13px'
      }),
      multiValueLabel: (provided) => ({
        ...provided,
        fontSize: isMobile ? '12px' : '13px',
        color: '#0d47a1',
        padding: isMobile ? '1px 4px' : '2px 6px',
        fontWeight: '400'
      }),
      multiValueRemove: (provided) => ({
        ...provided,
        color: '#0d47a1',
        borderRadius: '0 3px 3px 0',
        ':hover': { backgroundColor: '#dc3545', color: 'white' },
        padding: isMobile ? '1px' : '2px',
        fontSize: isMobile ? '13px' : '14px'
      }),
      placeholder: (provided) => ({
        ...provided,
        color: '#adb5bd',
        fontSize: isMobile ? '13px' : '14px'
      })
    };
  }

  open() {
    this.isMobile = window.innerWidth < MOBILE_BREAKPOINT;
    const position = this.calculateDropdownPosition();

    Object.assign(this.selectContainer.style, {
      top: `${position.top}px`,
      left: `${position.left}px`,
      width: `${position.width}px`,
      maxHeight: `${position.maxHeight}px`,
      overflowY: 'visible'
    });

    // Ensure the container doesn't exceed viewport bounds
    const containerRect = {
      top: position.top,
      left: position.left,
      width: position.width,
      height: position.maxHeight
    };

    // Adjust if container goes beyond viewport
    if (containerRect.top + containerRect.height > window.innerHeight + window.scrollY) {
      const adjustedTop = Math.max(window.scrollY + 10, window.innerHeight + window.scrollY - containerRect.height - 10);
      this.selectContainer.style.top = `${adjustedTop}px`;
    }

    this.hot.rootDocument.body.appendChild(this.selectContainer);
    this.root = ReactDOM.createRoot(this.selectContainer);
    this.setupScrollListeners();

    // Prepare data
    let sourceData = this.cellProperties.source;
    if (typeof sourceData === 'function') {
      sourceData = sourceData.call(this);
    }

    this.options = sourceData.map(item => ({
      ...item,
      value: item.id,
      label: item.name,
    }));

    const initialValueIds = (this.initialValue || '').split(',').filter(Boolean);
    let initialOptions = [];
    if (!this.agreeAll) {
      initialOptions = this.options.filter(option =>
        initialValueIds.includes(option.value.toString())
      );
    }

    this.currentValue = initialOptions;
    this.currentWeight = this.currentValue.reduce((sum, option) => sum + (option.updated_weight || 0), 0);
    const headerControlHeight = this.isMobile ? 77 : 87; // Header + control heights
    this.maxMenuHeight = Math.max(position.maxHeight - headerControlHeight, this.isMobile ? 180 : 200);

    this.renderReactComponent();
  }

  renderReactComponent() {
    this.headerDiv = this.createHeader();
    this.updateSelectionCount(this.currentWeight);
    this.updateProgressBar(this.currentWeight);

    const editor = this;

    this.root.render(
      <div style={{ position: 'relative' }}>
        <Select
          key={this.renderKey}
          ref={ref => (this.selectRef = ref)}
          isMulti
          isClearable
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          defaultValue={this.currentValue}
          options={this.options}
          styles={this.getSelectStyles(this.maxMenuHeight)}
          menuIsOpen={true}
          classNamePrefix="cq-select"
          onKeyDown={(e) => this.handleKeyDown(e, editor)}
          onChange={(selectedOptions) => {
            const wasAgreeAll = this.agreeAll;
            if (wasAgreeAll) {
              this.agreeAll = false;
            }

            const newSelections = selectedOptions || [];
            const isAdding = newSelections.length > (this.currentValue || []).length;

            this.currentValue = newSelections;
            this.currentWeight = this.currentValue.reduce((sum, option) => sum + (option.updated_weight || 0), 0);

            this.updateSelectionCount(this.currentWeight);
            this.updateProgressBar(this.currentWeight);
            this.clearWarningMessage();

            if (isAdding) {
              setTimeout(() => {
                const valueContainer = this.selectContainer.querySelector('.cq-select__value-container');
                if (valueContainer) {
                  valueContainer.scrollTop = valueContainer.scrollHeight;
                }
              }, 0);
            }

            if (wasAgreeAll) {
              this.renderKey++;
              this.renderReactComponent();
            }
          }}
          isOptionDisabled={(option) => {
            if (this.agreeAll) {
              return true;
            }
            const currentSelections = this.currentValue || [];
            const isAlreadySelected = currentSelections.some(selected =>
              selected.value === option.value
            );
            const currentWeight = this.currentWeight || 0;
            return !isAlreadySelected && currentWeight + (option.updated_weight || 0) > 10;
          }}
          placeholder="Select up to 10 points..."
          noOptionsMessage={() => "No issues available"}
          menuPlacement="auto"
          components={{
            Control: props => (editor.agreeAll ? null : <components.Control {...props} />),
            DropdownIndicator: () => null,
            IndicatorSeparator: () => null,
            Menu: (props) => (
              <div {...props.innerProps} style={props.getStyles('menu', props)}>
                <div ref={ref => {
                  if (ref && !ref.hasChildNodes()) {
                    ref.appendChild(editor.headerDiv);
                  }
                }} />
                {this.agreeAll ? (
                  <div style={{
                    padding: '16px',
                    textAlign: 'center',
                    color: '#6c757d',
                    fontSize: '14px',
                    borderTop: '1px solid #e9ecef'
                  }}>
                    All audits for this case have been agreed.
                  </div>
                ) : props.children}
              </div>
            ),
            MultiValueRemove: props => (
              <components.MultiValueRemove
                {...props}
                innerProps={{
                  ...props.innerProps,
                  onMouseDown: e => {
                    e.stopPropagation();
                    props.innerProps.onMouseDown(e);
                  },
                }}
              />
            ),
            Option: ({ children, ...props }) => {
              const { isDisabled, data } = props;
              const innerProps = { ...props.innerProps };

              if (isDisabled) {
                innerProps.onClick = (e) => {
                  e.preventDefault();
                  if (this.agreeAll) return;
                  editor.showWarningMessage('Total weight cannot exceed 10 points');
                };
              }

              return (
                <div
                  {...innerProps}
                  style={{
                    ...props.getStyles('option', props),
                    opacity: isDisabled ? 0.6 : 1,
                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <span>{children}</span>
                  {data.updated_weight !== undefined && (
                    <span style={{
                      backgroundColor: '#e9ecef',
                      color: '#495057',
                      borderRadius: '4px',
                      padding: '2px 6px',
                      fontSize: '11px',
                      fontWeight: '500',
                      marginLeft: '8px'
                    }}>{data.updated_weight}</span>
                  )}
                </div>
              );
            }
          }}
        />
      </div>
    );
  }

  showWarningMessage(message, duration = 2500) {
    this.clearWarningMessage();

    this.warningDiv = document.createElement('div');
    const { top, left, width } = this.selectContainer.getBoundingClientRect();

    const isMobile = this.isMobile;
    Object.assign(this.warningDiv.style, {
      position: 'absolute',
      top: `${window.scrollY + top + (isMobile ? 60 : 70)}px`,
      left: `${window.scrollX + left + (isMobile ? 8 : 10)}px`,
      width: `${width - (isMobile ? 16 : 20)}px`,
      padding: isMobile ? '6px 10px' : '8px 12px',
      backgroundColor: '#fff3cd',
      color: '#856404',
      textAlign: 'center',
      fontSize: isMobile ? '11px' : '12px',
      fontWeight: '500',
      border: '1px solid #ffeaa7',
      borderRadius: '4px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
      zIndex: '99999'
    });

    this.warningDiv.innerText = message;
    document.body.appendChild(this.warningDiv);

    this.warningTimeout = setTimeout(() => {
      this.clearWarningMessage();
    }, duration);
  }

  close() {
    this.clearWarningMessage();
    this.removeScrollListeners();

    if (this.root) {
      this.root.unmount();
    }
    if (this.selectContainer?.isConnected) {
      this.selectContainer.remove();
    }
  }

  focus() {
    this.selectRef?.focus();
  }

  finishEditing(revertToOriginal = false) {
    if (this.isOpened) {
      super.finishEditing(revertToOriginal);
    }
  }
}

export const generateOptions = (rowData, responseAuditIssues) => {
  const baseOptions = responseAuditIssues || [];
  if (!baseOptions.length) return [];

  const allOptions = [];
  const rowCodes = { cpt: new Set(), icd: new Set(), modifier: new Set() };

  // Add static options
  baseOptions.forEach(option => {
    if (!option.is_dynamic) {
      allOptions.push(option);
    }
  });

  // Extract codes from row data
  for (const [key, value] of Object.entries(rowData)) {
    if (!value) continue;

    const cleanValue = String(value).split('*')[0];

    // if (key.match(/^(srvcs_no|billed_cpt|audited_cpt)/)) {
    if (key.match(/^(srvcs_no)/)) {
      rowCodes.cpt.add(cleanValue);
      // } else if (key.match(/^(billed_icd|audited_icd)/)) {
    } else if (key.match(/^(billed_icd)/)) {
      rowCodes.icd.add(cleanValue);
      // } else if (key.match(/^(billed_modifier|audited_modifier)/)) {
    } else if (key.match(/^(billed_modifier)/)) {
      String(value).split(',').forEach(m => rowCodes.modifier.add(m.trim()));
    }
  }

  // Add dynamic options
  baseOptions.forEach(option => {
    if (option.is_dynamic && option.type && rowCodes[option.type]) {
      if (option.type === 'modifier') {
        let count = 1;
        rowCodes.modifier.forEach(code => {
          if (code) {
            allOptions.push({
              ...option,
              id: `${option.id}:${code}`,
              name: `${option.name} ${count++}`,
            });
          }
        });
      } else {
        rowCodes[option.type].forEach(code => {
          if (code) {
            allOptions.push({
              ...option,
              id: `${option.id}:${code}`,
              name: `${option.name}: ${code}`,
            });
          }
        });
      }
    }
  });

  return allOptions;
};

export const decodeResponseValue = (value, rowData, responseAuditIssues) => {
  if (!value) return '';
  if (String(value) === AGREE_AUDITS_VALUE) return AGREE_AUDITS_LABEL;
  const options = generateOptions(rowData, responseAuditIssues);
  if (!options.length) return '';
  const ids = String(value).split(',');
  const decoded = ids.map(id => {
    const option = options.find(opt => String(opt.id) === String(id));
    if (option) return option.name;
    const baseId = String(id).split(':')[0];
    if (baseId !== String(id)) {
      const baseOption = options.find(opt => String(opt.id).split(':')[0] === baseId);
      if (baseOption) return baseOption.name;
    }
    return null;
  }).filter(Boolean);
  return decoded.length ? decoded.join(', ') : '';
};

export const responseRenderer = (instance, td, row, col, prop, value, cellProperties, responseAuditIssues) => {
  Handsontable.renderers.TextRenderer.apply(this, [instance, td, row, col, prop, value, cellProperties]);

  if (value === AGREE_AUDITS_VALUE) {
    td.innerText = AGREE_AUDITS_LABEL;
  } else if (value) {
    const rowData = instance.getSourceDataAtRow(row);
    const decoded = decodeResponseValue(value, rowData, responseAuditIssues);
    td.innerText = decoded || String(value);
  } else {
    td.innerText = '';
  }

  Object.assign(td.style, {
    textAlign: 'center',
    verticalAlign: 'middle'
  });

  return td;
};